"use client";
import React from "react";
import {
  Mail,
  Phone,
  MapPin,
  Facebook,
  Twitter,
  Linkedin,
  Instagram,
  Send,
  Award,
  Shield,
  Leaf,
  Sparkles,
} from "lucide-react";
import { useTranslation } from "react-i18next";
import Link from "next/link";
import { Button } from "./ui/button";

export const Footer: React.FC = () => {
  const { t } = useTranslation("common");
  const currentYear = new Date().getFullYear();

  return (
    <footer className="relative mt-32">
      {/* Newsletter Section - Elevated above footer */}
      <div className="relative -mb-24 px-4 sm:px-6 lg:px-8 z-20">
        <div className="max-w-7xl mx-auto">
          <div className="backdrop-blur-xl bg-gradient-to-br from-walnut-600 to-walnut-800 dark:from-walnut-500 dark:to-walnut-700 rounded-[33px] p-8 md:p-12 shadow-2xl shadow-walnut-500/20">
            <div className="grid md:grid-cols-2 gap-8 items-center">
              <div>
                <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/20 backdrop-blur-xl mb-4">
                  <Sparkles className="w-4 h-4 text-white" />
                  <span className="text-white text-sm font-medium">
                    {t("footer.newsletter.badge")}
                  </span>
                </div>
                <h3 className="text-3xl md:text-4xl font-bold text-white mb-3">
                  {t("footer.newsletter.title")}
                </h3>
                <p className="text-white/90 text-lg">
                  {t("footer.newsletter.subtitle")}
                </p>
              </div>
              <div>
                <div className="flex flex-col sm:flex-row gap-3">
                  <input
                    type="email"
                    placeholder={t("footer.newsletter.placeholder")}
                    className="flex-1 px-6 py-4 rounded-[20px] backdrop-blur-xl bg-white/20 border border-white/30 text-white placeholder-white/70 focus:outline-none focus:border-white/50 transition-all"
                  />
                  <Button variant="secondary" size="lg" pill className="bg-white hover:bg-white/90 text-walnut-700 shadow-lg hover:scale-105">
                    <Send className="w-5 h-5" />
                    {t("footer.newsletter.button")}
                  </Button>
                </div>
                <p className="text-white/70 text-sm mt-3">
                  {t("footer.newsletter.privacy")}{" "}
                  <Link
                    href="/privacy"
                    className="underline hover:text-white transition-colors"
                  >
                    {t("footer.newsletter.privacyLink")}
                  </Link>
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Main Footer */}
      <div className="relative backdrop-blur-[5px] bg-white/10 dark:bg-white/5 glass-shadow-outer pt-32 pb-8">
        {/* Inset shadows for glassmorphism depth */}
        <div className="absolute inset-0 pointer-events-none glass-shadow-inset rounded-t-[25px] rounded-b-[0px] bg-transparent" />

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          {/* Main Footer Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-12 gap-8 lg:gap-12 mb-12">
            {/* Company Info - Larger section */}
            <div className="lg:col-span-4">
              <div className="flex items-center space-x-3 mb-6">
                <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-walnut-600 to-walnut-800 dark:from-walnut-500 dark:to-walnut-700 flex items-center justify-center shadow-xl">
                  <span className="text-white font-bold text-2xl">W</span>
                </div>
                <div>
                  <div className="font-bold text-lg text-walnut-900 dark:text-white">
                    {t("footer.companyName")}
                  </div>
                  <div className="text-sm text-walnut-600 dark:text-walnut-300">
                    {t("footer.companySubtitle")}
                  </div>
                </div>
              </div>
              <p className="text-walnut-700 dark:text-walnut-300 mb-6 leading-relaxed">
                {t("footer.tagline")}
              </p>

              {/* Trust Badges */}
              <div className="grid grid-cols-3 gap-3 mb-6">
                <div className="backdrop-blur-xl bg-white/20 dark:bg-black/20 rounded-xl p-3 text-center">
                  <Award className="w-6 h-6 text-walnut-600 dark:text-walnut-400 mx-auto mb-1" />
                  <div className="text-xs text-walnut-700 dark:text-walnut-300 font-medium">
                    {t("footer.badges.iso")}
                  </div>
                </div>
                <div className="backdrop-blur-xl bg-white/20 dark:bg-black/20 rounded-xl p-3 text-center">
                  <Leaf className="w-6 h-6 text-walnut-600 dark:text-walnut-400 mx-auto mb-1" />
                  <div className="text-xs text-walnut-700 dark:text-walnut-300 font-medium">
                    {t("footer.badges.fsc")}
                  </div>
                </div>
                <div className="backdrop-blur-xl bg-white/20 dark:bg-black/20 rounded-xl p-3 text-center">
                  <Shield className="w-6 h-6 text-walnut-600 dark:text-walnut-400 mx-auto mb-1" />
                  <div className="text-xs text-walnut-700 dark:text-walnut-300 font-medium">
                    {t("footer.badges.fda")}
                  </div>
                </div>
              </div>

              {/* Social Links */}
              <div className="flex space-x-3">
                <a
                  href="#"
                  className="w-11 h-11 rounded-xl backdrop-blur-xl bg-white/20 dark:bg-white/10 hover:bg-walnut-600 dark:hover:bg-walnut-500 flex items-center justify-center transition-all duration-300 hover:scale-110 hover:shadow-lg group"
                >
                  <Facebook className="w-5 h-5 text-walnut-700 dark:text-walnut-300 group-hover:text-white" />
                </a>
                <a
                  href="#"
                  className="w-11 h-11 rounded-xl backdrop-blur-xl bg-white/20 dark:bg-white/10 hover:bg-walnut-600 dark:hover:bg-walnut-500 flex items-center justify-center transition-all duration-300 hover:scale-110 hover:shadow-lg group"
                >
                  <Twitter className="w-5 h-5 text-walnut-700 dark:text-walnut-300 group-hover:text-white" />
                </a>
                <a
                  href="#"
                  className="w-11 h-11 rounded-xl backdrop-blur-xl bg-white/20 dark:bg-white/10 hover:bg-walnut-600 dark:hover:bg-walnut-500 flex items-center justify-center transition-all duration-300 hover:scale-110 hover:shadow-lg group"
                >
                  <Linkedin className="w-5 h-5 text-walnut-700 dark:text-walnut-300 group-hover:text-white" />
                </a>
                <a
                  href="#"
                  className="w-11 h-11 rounded-xl backdrop-blur-xl bg-white/20 dark:bg-white/10 hover:bg-walnut-600 dark:hover:bg-walnut-500 flex items-center justify-center transition-all duration-300 hover:scale-110 hover:shadow-lg group"
                >
                  <Instagram className="w-5 h-5 text-walnut-700 dark:text-walnut-300 group-hover:text-white" />
                </a>
              </div>
            </div>

            {/* Quick Links */}
            <div className="lg:col-span-2">
              <h3 className="font-bold text-walnut-900 dark:text-white mb-6 text-lg">
                {t("footer.quickLinks.title")}
              </h3>
              <ul className="space-y-3">
                <li>
                  <Link
                    href="/"
                    className="text-walnut-700 dark:text-walnut-300 hover:text-walnut-900 dark:hover:text-white transition-colors duration-300 flex items-center group"
                  >
                    <span className="w-1.5 h-1.5 rounded-full bg-walnut-400 dark:bg-walnut-500 mr-3 group-hover:bg-walnut-600 dark:group-hover:bg-walnut-300 transition-colors"></span>
                    {t("footer.quickLinks.home")}
                  </Link>
                </li>
                <li>
                  <Link
                    href="/about"
                    className="text-walnut-700 dark:text-walnut-300 hover:text-walnut-900 dark:hover:text-white transition-colors duration-300 flex items-center group"
                  >
                    <span className="w-1.5 h-1.5 rounded-full bg-walnut-400 dark:bg-walnut-500 mr-3 group-hover:bg-walnut-600 dark:group-hover:bg-walnut-300 transition-colors"></span>
                    {t("footer.quickLinks.about")}
                  </Link>
                </li>
                <li>
                  <Link
                    href="/products"
                    className="text-walnut-700 dark:text-walnut-300 hover:text-walnut-900 dark:hover:text-white transition-colors duration-300 flex items-center group"
                  >
                    <span className="w-1.5 h-1.5 rounded-full bg-walnut-400 dark:bg-walnut-500 mr-3 group-hover:bg-walnut-600 dark:group-hover:bg-walnut-300 transition-colors"></span>
                    {t("footer.quickLinks.products")}
                  </Link>
                </li>
                <li>
                  <Link
                    href="/industries"
                    className="text-walnut-700 dark:text-walnut-300 hover:text-walnut-900 dark:hover:text-white transition-colors duration-300 flex items-center group"
                  >
                    <span className="w-1.5 h-1.5 rounded-full bg-walnut-400 dark:bg-walnut-500 mr-3 group-hover:bg-walnut-600 dark:group-hover:bg-walnut-300 transition-colors"></span>
                    {t("footer.quickLinks.industries")}
                  </Link>
                </li>
                <li>
                  <Link
                    href="/diecuts"
                    className="text-walnut-700 dark:text-walnut-300 hover:text-walnut-900 dark:hover:text-white transition-colors duration-300 flex items-center group"
                  >
                    <span className="w-1.5 h-1.5 rounded-full bg-walnut-400 dark:bg-walnut-500 mr-3 group-hover:bg-walnut-600 dark:group-hover:bg-walnut-300 transition-colors"></span>
                    {t("footer.quickLinks.diecuts")}
                  </Link>
                </li>
                <li>
                  <Link
                    href="/blog"
                    className="text-walnut-700 dark:text-walnut-300 hover:text-walnut-900 dark:hover:text-white transition-colors duration-300 flex items-center group"
                  >
                    <span className="w-1.5 h-1.5 rounded-full bg-walnut-400 dark:bg-walnut-500 mr-3 group-hover:bg-walnut-600 dark:group-hover:bg-walnut-300 transition-colors"></span>
                    {t("footer.quickLinks.blog")}
                  </Link>
                </li>
              </ul>
            </div>

            {/* Resources */}
            <div className="lg:col-span-3">
              <h3 className="font-bold text-walnut-900 dark:text-white mb-6 text-lg">
                {t("footer.resources.title")}
              </h3>
              <ul className="space-y-3">
                <li>
                  <Link
                    href="/sustainability"
                    className="text-walnut-700 dark:text-walnut-300 hover:text-walnut-900 dark:hover:text-white transition-colors duration-300 flex items-center group"
                  >
                    <span className="w-1.5 h-1.5 rounded-full bg-walnut-400 dark:bg-walnut-500 mr-3 group-hover:bg-walnut-600 dark:group-hover:bg-walnut-300 transition-colors"></span>
                    {t("footer.resources.sustainability")}
                  </Link>
                </li>
                <li>
                  <Link
                    href="/manufacturing"
                    className="text-walnut-700 dark:text-walnut-300 hover:text-walnut-900 dark:hover:text-white transition-colors duration-300 flex items-center group"
                  >
                    <span className="w-1.5 h-1.5 rounded-full bg-walnut-400 dark:bg-walnut-500 mr-3 group-hover:bg-walnut-600 dark:group-hover:bg-walnut-300 transition-colors"></span>
                    {t("footer.resources.manufacturing")}
                  </Link>
                </li>
                <li>
                  <Link
                    href="/faq"
                    className="text-walnut-700 dark:text-walnut-300 hover:text-walnut-900 dark:hover:text-white transition-colors duration-300 flex items-center group"
                  >
                    <span className="w-1.5 h-1.5 rounded-full bg-walnut-400 dark:bg-walnut-500 mr-3 group-hover:bg-walnut-600 dark:group-hover:bg-walnut-300 transition-colors"></span>
                    {t("footer.resources.faq")}
                  </Link>
                </li>
                <li>
                  <Link
                    href="/contact"
                    className="text-walnut-700 dark:text-walnut-300 hover:text-walnut-900 dark:hover:text-white transition-colors duration-300 flex items-center group"
                  >
                    <span className="w-1.5 h-1.5 rounded-full bg-walnut-400 dark:bg-walnut-500 mr-3 group-hover:bg-walnut-600 dark:group-hover:bg-walnut-300 transition-colors"></span>
                    {t("footer.resources.contact")}
                  </Link>
                </li>
                <li>
                  <Link
                    href="/quote"
                    className="text-walnut-700 dark:text-walnut-300 hover:text-walnut-900 dark:hover:text-white transition-colors duration-300 flex items-center group"
                  >
                    <span className="w-1.5 h-1.5 rounded-full bg-walnut-400 dark:bg-walnut-500 mr-3 group-hover:bg-walnut-600 dark:group-hover:bg-walnut-300 transition-colors"></span>
                    {t("footer.resources.quote")}
                  </Link>
                </li>
              </ul>
            </div>

            {/* Contact Info */}
            <div className="lg:col-span-3">
              <h3 className="font-bold text-walnut-900 dark:text-white mb-6 text-lg">
                {t("footer.contact.title")}
              </h3>
              <ul className="space-y-4">
                <li className="flex items-start space-x-3 group">
                  <div className="w-10 h-10 rounded-xl backdrop-blur-xl bg-walnut-100 dark:bg-walnut-800 flex items-center justify-center flex-shrink-0 group-hover:bg-walnut-200 dark:group-hover:bg-walnut-700 transition-colors">
                    <MapPin className="w-5 h-5 text-walnut-600 dark:text-walnut-400" />
                  </div>
                  <div>
                    <div className="text-xs text-walnut-600 dark:text-walnut-400 mb-1">
                      {t("footer.contact.addressLabel")}
                    </div>
                    <span className="text-sm text-walnut-700 dark:text-walnut-300 leading-relaxed">
                      {t("footer.contact.address")}
                    </span>
                  </div>
                </li>
                <li className="flex items-start space-x-3 group">
                  <div className="w-10 h-10 rounded-xl backdrop-blur-xl bg-walnut-100 dark:bg-walnut-800 flex items-center justify-center flex-shrink-0 group-hover:bg-walnut-200 dark:group-hover:bg-walnut-700 transition-colors">
                    <Phone className="w-5 h-5 text-walnut-600 dark:text-walnut-400" />
                  </div>
                  <div>
                    <div className="text-xs text-walnut-600 dark:text-walnut-400 mb-1">
                      {t("footer.contact.phoneLabel")}
                    </div>
                    <a
                      href={`tel:${t("footer.contact.phone").replace(/\s/g, "")}`}
                      className="text-sm text-walnut-700 dark:text-walnut-300 hover:text-walnut-900 dark:hover:text-white transition-colors"
                    >
                      {t("footer.contact.phone")}
                    </a>
                  </div>
                </li>
                <li className="flex items-start space-x-3 group">
                  <div className="w-10 h-10 rounded-xl backdrop-blur-xl bg-walnut-100 dark:bg-walnut-800 flex items-center justify-center flex-shrink-0 group-hover:bg-walnut-200 dark:group-hover:bg-walnut-700 transition-colors">
                    <Mail className="w-5 h-5 text-walnut-600 dark:text-walnut-400" />
                  </div>
                  <div>
                    <div className="text-xs text-walnut-600 dark:text-walnut-400 mb-1">
                      {t("footer.contact.emailLabel")}
                    </div>
                    <a
                      href={`mailto:${t("footer.contact.email")}`}
                      className="text-sm text-walnut-700 dark:text-walnut-300 hover:text-walnut-900 dark:hover:text-white transition-colors"
                    >
                      {t("footer.contact.email")}
                    </a>
                  </div>
                </li>
              </ul>
            </div>
          </div>

          {/* Bottom Bar */}
          <div className="pt-8 border-t border-white/20 dark:border-white/10">
            <div className="flex flex-col md:flex-row justify-between items-center space-y-4 md:space-y-0">
              <p className="text-sm text-walnut-600 dark:text-walnut-400">
                {t("footer.copyright").replace(
                  "{year}",
                  currentYear.toString(),
                )}
              </p>
              <div className="flex items-center space-x-6">
                <Link
                  href="/privacy"
                  className="text-sm text-walnut-600 dark:text-walnut-400 hover:text-walnut-900 dark:hover:text-white transition-colors duration-300"
                >
                  {t("footer.legal.privacy")}
                </Link>
                <Link
                  href="/terms"
                  className="text-sm text-walnut-600 dark:text-walnut-400 hover:text-walnut-900 dark:hover:text-white transition-colors duration-300"
                >
                  {t("footer.legal.terms")}
                </Link>
                <Link
                  href="/faq"
                  className="text-sm text-walnut-600 dark:text-walnut-400 hover:text-walnut-900 dark:hover:text-white transition-colors duration-300"
                >
                  {t("footer.legal.faq")}
                </Link>
              </div>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};
